import { ACTION_META } from "./meta/action.ts";
import {
	ACTION_SPECS,
	CONDITION_SPECS,
	EVENT_SPECS,
	type ActionSpec,
} from "./schemaSpec.ts";
import { UI_PROPERTY_REGISTRY } from "./uiPropertyRegistry.ts";
import {
	UiNodeType,
	UI_TREE_ROLES,
	PLUGIN_OWNED_FIELDS,
	PLUGIN_OWNED_GRAPH_FIELDS,
} from "./entities.ts";
import { GET_REGISTRY } from "./getRegistry.ts";
import { CONTEXT_REGISTRY } from "./contextRegistry.ts";
import { MATH_OPS } from "./mathRegistry.ts";
import { COMPARE_OPS } from "./compareRegistry.ts";
import { ENTITY_SPECS } from "./entitySpec.ts";

export const AGENT_MANIFEST_VERSION = "agent-manifest-v1";

export type AgentActionExposure =
	| "public"
	| "advanced"
	| "internal"
	| "deprecated";

type JsonValue =
	| null
	| boolean
	| number
	| string
	| JsonValue[]
	| { [key: string]: JsonValue };

type AgentManifestOptions = {
	includeInternal?: boolean;
};

const ACTION_META_BY_TYPE = new Map(
	ACTION_META.map((meta) => [meta.type, meta]),
);

function normalizeJson(value: unknown): JsonValue {
	if (value === null) return null;
	if (typeof value === "string") return value;
	if (typeof value === "number") return Number.isFinite(value) ? value : null;
	if (typeof value === "boolean") return value;
	if (Array.isArray(value)) return value.map((entry) => normalizeJson(entry));
	if (typeof value === "object") {
		const out: { [key: string]: JsonValue } = {};
		for (const key of Object.keys(value as Record<string, unknown>).sort()) {
			const entry = (value as Record<string, unknown>)[key];
			if (entry === undefined || typeof entry === "function") continue;
			out[key] = normalizeJson(entry);
		}
		return out;
	}
	return null;
}

export function stableStringify(value: unknown): string {
	return JSON.stringify(normalizeJson(value));
}

export function hashStableString(value: string): string {
	let hash = 0x811c9dc5;
	for (let i = 0; i < value.length; i += 1) {
		hash ^= value.charCodeAt(i);
		hash = Math.imul(hash, 0x01000193);
	}
	return (hash >>> 0).toString(16).padStart(8, "0");
}

function getActionExposure(spec: ActionSpec): AgentActionExposure {
	return ACTION_META_BY_TYPE.get(spec.type)?.picker?.exposure ?? "public";
}

function shouldExposeAction(
	spec: ActionSpec,
	options: AgentManifestOptions,
): boolean {
	const exposure = getActionExposure(spec);
	if (exposure === "deprecated") return Boolean(options.includeInternal);
	if (exposure === "internal") return Boolean(options.includeInternal);
	return true;
}

export function buildAgentManifest(options: AgentManifestOptions = {}) {
	const actions = ACTION_SPECS
		.filter((spec) => shouldExposeAction(spec, options))
		.map((spec) => {
			const meta = ACTION_META_BY_TYPE.get(spec.type) ?? null;
			return {
				...spec,
				exposure: getActionExposure(spec),
				meta,
			};
		});

	return {
		version: AGENT_MANIFEST_VERSION,
		actions,
		entities: ENTITY_SPECS,
		events: EVENT_SPECS,
		conditions: CONDITION_SPECS,
		ui: {
			nodeTypes: UiNodeType.options,
			roles: UI_TREE_ROLES,
			properties: UI_PROPERTY_REGISTRY,
		},
		data: {
			getSources: GET_REGISTRY,
			contextScopes: CONTEXT_REGISTRY,
			mathOperators: MATH_OPS,
			compareOperators: COMPARE_OPS,
		},
		policies: {
			pluginOwnedFields: PLUGIN_OWNED_FIELDS,
			pluginOwnedGraphFields: PLUGIN_OWNED_GRAPH_FIELDS,
			defaultActionExposure: ["public", "advanced"] as const,
		},
	};
}

export type AgentManifest = ReturnType<typeof buildAgentManifest>;

export function getAgentManifestHash(
	options: AgentManifestOptions = {},
): string {
	return hashStableString(stableStringify(buildAgentManifest(options)));
}

export const AGENT_MANIFEST = buildAgentManifest();
export const AGENT_SCHEMA_HASH = getAgentManifestHash();
